import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
	MAT_DIALOG_DATA,
	MatDialogActions,
	MatDialogClose,
	MatDialogContent,
	MatDialogRef,
	MatDialogTitle,
} from "@angular/material/dialog";
import { MatButton } from "@angular/material/button";
import { MatFormField, MatHint, MatLabel, MatPrefix } from "@angular/material/form-field";
import { MatIcon } from "@angular/material/icon";
import { MatInput } from "@angular/material/input";
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { TranslatePipe, TranslateService } from "@tolgee/ngx";
import { finalize, first } from "rxjs";
import { NotificationService } from "../../../../services/notification.service";
import {
	PROTOCOL_NOTE_CONTENT_MAX_LENGTH,
	ProtocolNote,
	ProtocolNoteRequest,
	buildProtocolNoteRequest,
	isProtocolNoteContentValid,
} from "../entities/protocol-note";
import { EMPTY_PROTOCOL_NOTE_ANCHOR, ProtocolNoteAnchor } from "../entities/protocol-note-anchor";
import { ProtocolNoteService } from "../protocol-note.service";

export interface ProtocolNoteDialogData {
	protocolId: number;
	selectedText?: string | null;
	anchor?: ProtocolNoteAnchor;
	/** Présente en édition, absente en création. */
	note?: ProtocolNote;
}

@Component({
	selector: "app-protocol-note-dialog",
	imports: [
		MatDialogTitle,
		MatDialogContent,
		MatDialogActions,
		MatDialogClose,
		MatButton,
		MatProgressSpinner,
		MatFormField,
		MatLabel,
		MatHint,
		MatInput,
		MatIcon,
		MatPrefix,
		FormsModule,
		TranslatePipe,
	],
	templateUrl: "./protocol-note-dialog.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./protocol-note-dialog.component.scss",
})
export class ProtocolNoteDialogComponent {
	readonly data = inject<ProtocolNoteDialogData>(MAT_DIALOG_DATA);
	private dialogRef = inject(MatDialogRef<ProtocolNoteDialogComponent>);
	private noteService = inject(ProtocolNoteService);
	private toastr = inject(NotificationService);
	private translate = inject(TranslateService);

	readonly maxLength = PROTOCOL_NOTE_CONTENT_MAX_LENGTH;
	readonly isEdit: boolean;
	readonly selectedText: string;

	content = "";
	isLoading = false;

	constructor() {
		this.isEdit = this.data?.note != null;
		this.content = this.data?.note?.content ?? "";
		this.selectedText = (this.isEdit ? this.data.note.selected_text : this.data?.selectedText) ?? "";
	}

	get isReady(): boolean {
		return !this.isLoading && isProtocolNoteContentValid(this.content);
	}

	get isTooLong(): boolean {
		return (this.content?.trim().length ?? 0) > this.maxLength;
	}

	get length(): number {
		return this.content?.trim().length ?? 0;
	}

	save(): void {
		if (!this.isReady) {
			return;
		}

		this.isLoading = true;
		const request = this.isEdit
			? this.noteService.updateNote(this.data.note.id, buildProtocolNoteRequest(this.data.note, this.content))
			: this.noteService.createProtocolNote(this.data.protocolId, this.buildCreateRequest());

		request
			.pipe(
				first(),
				finalize(() => (this.isLoading = false))
			)
			.subscribe({
				next: (note) => {
					this.toastr.success(
						this.translate.instant(this.isEdit ? "protocols.notes.updated" : "protocols.notes.created")
					);
					this.dialogRef.close(note);
				},
				// Les erreurs de validation remontent en 405 et sont déjà affichées
				// par l'intercepteur : on garde seulement le dialogue ouvert.
				error: () => undefined,
			});
	}

	private buildCreateRequest(): ProtocolNoteRequest {
		return {
			content: this.content.trim(),
			selectedText: this.selectedText || null,
			selectionAnchor: this.data?.anchor ?? EMPTY_PROTOCOL_NOTE_ANCHOR,
		};
	}
}
